// One source of truth for turning a mean score into the wording HR uses. Both
// the summary editor and the published/results views map the mean to a scale
// point and read the matching `perfReview.expectationScale` label
// ("3.2/5 · Соответствует ожиданиям"), so the same number never gets two
// different verdicts across screens.
//
// Colours mirror the five-point sentiment ramp; non-five-point scales stay
// neutral because the labels only make sense on the expectation scale.
export const SCALE_SENTIMENT_5 = ['red', 'orange', 'green', 'teal', 'grape'] as const;

export const scaleTone = (position: number, count: number): string =>
  count === 5 ? (SCALE_SENTIMENT_5[position - 1] ?? 'gray') : 'gray';

export interface ScoreMeta {
  label?: string;
  color: string;
}

// The band for point n runs [n-0.3, n+0.7): "meets expectations" (3) covers
// 2.7 up to 3.7, so a middling 2.5 reads as "below" rather than being rounded
// up to "meets". That is `round(mean - 0.2)`, clamped to the scale.
// Итог по всему ревью: среднее по КАЖДОЙ выставленной оценке, а не среднее
// средних. Блок с двумя ответами не должен весить столько же, сколько блок с
// восемью, поэтому взвешиваем блочные средние на число ответов — получается
// ровно то же число, что и при усреднении сырых оценок.
export const weightedMean = (
  rows: { scoreMean?: number; responseCount?: number }[],
): { mean: number; count: number } | null => {
  let sum = 0;
  let count = 0;
  for (const r of rows) {
    if (typeof r.scoreMean !== 'number') continue;
    const n = r.responseCount ?? 0;
    if (n <= 0) continue;
    sum += r.scoreMean * n;
    count += n;
  }
  return count > 0 ? { mean: sum / count, count } : null;
};

export const scoreExpectation = (mean: number, labels?: string[], min = 1, max = 5): ScoreMeta => {
  const count = max - min + 1;
  const point = Math.min(max, Math.max(min, Math.round(mean - 0.2)));
  return {
    label: labels?.[point - min],
    color: scaleTone(point - min + 1, count),
  };
};
